// ═══════════════════════════════════════════════════════════════════
// TMS GAMMA — Observabilidade
//
// Sentry para erro e desempenho no front. OpenTelemetry como camada
// neutra de tracing, carregada sob demanda.
//
// Foram propostos quatro fornecedores (Sentry, Datadog, New Relic, OTel).
// Eles COMPETEM, não se somam: os quatro no bundle dariam ~120 kB gzip a
// mais e devolveriam quase toda a redução de 481 → 136 kB da #13, para
// mostrar o mesmo erro em quatro painéis e pagar três contratos.
//
// A dupla escolhida cobre o problema real — ninguém fica sabendo quando o
// sistema quebra na máquina do operador — e mantém a porta aberta: se um
// dia a empresa quiser Datadog ou New Relic, muda-se o endpoint do OTel,
// não a instrumentação.
//
// NADA É ENVIADO sem variável de ambiente configurada. Quem clona o
// repositório e roda local não manda telemetria para lugar nenhum.
//
// O Sentry é carregado por import DINÂMICO. Medido: com import estático,
// o @sentry/react v10 custa +158 kB gzip no bundle inicial — mais que a
// aplicação inteira depois do code splitting da #13. Em chunk separado,
// ele entra depois da primeira pintura e não atrasa o que o usuário veio
// fazer. O custo é uma janela de alguns milissegundos no boot em que um
// erro não é capturado; o handler de window abaixo cobre o essencial.
// ═══════════════════════════════════════════════════════════════════

/** Módulo do Sentry depois de carregado. Null enquanto não houver DSN. */
let S = null;

const DSN      = process.env.REACT_APP_SENTRY_DSN;
const AMBIENTE = process.env.REACT_APP_AMBIENTE || "desenvolvimento";
const VERSAO   = process.env.REACT_APP_VERSAO   || "dev";
const OTLP     = process.env.REACT_APP_OTLP_ENDPOINT;

/**
 * Campos que NUNCA podem sair da máquina do usuário.
 *
 * A base tem CNPJ, razão social, valor de nota e endereço de entrega. Um
 * relatório de erro que carregue isso transforma a ferramenta de
 * diagnóstico em vazamento de dado de cliente — e o Sentry guarda os
 * eventos por semanas.
 */
const CAMPOS_SENSIVEIS = [
  "cnpj", "cpf", "razaoSocial", "razao_social", "nomeCliente", "nome_cliente",
  "email", "senha", "password", "token", "apikey", "api_key", "authorization",
  "vlrBruto", "vlr_bruto", "valorNota", "valor_nota", "endereco", "logradouro",
];

function limpar(obj, profundidade = 0) {
  if (profundidade > 4 || obj == null || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map((x) => limpar(x, profundidade + 1));

  const saida = {};
  for (const [k, v] of Object.entries(obj)) {
    const sensivel = CAMPOS_SENSIVEIS.some(
      (c) => k.toLowerCase() === c.toLowerCase()
    );
    saida[k] = sensivel ? "[removido]" : limpar(v, profundidade + 1);
  }
  return saida;
}

/** Tira query string de URL — é onde id de cliente costuma viajar. */
function urlSemQuery(url) {
  if (typeof url !== "string") return url;
  const i = url.indexOf("?");
  return i === -1 ? url : url.slice(0, i) + "?[removido]";
}

export async function iniciarObservabilidade() {
  if (!DSN) {
    // Silêncio proposital em desenvolvimento: aviso a cada F5 vira ruído
    // e some junto com o resto do console.
    if (AMBIENTE === "producao") {
      console.warn("[obs] REACT_APP_SENTRY_DSN ausente — erros não serão reportados.");
    }
    return;
  }

  S = await import("@sentry/react");

  S.init({
    dsn: DSN,
    environment: AMBIENTE,
    release: VERSAO,

    // Amostragem baixa de propósito. Em SPA, cada navegação vira
    // transação; 100% gera volume e custo sem trazer informação nova
    // depois das primeiras dezenas de amostras.
    tracesSampleRate: AMBIENTE === "producao" ? 0.1 : 0,

    // Session Replay desligado: gravaria a tela com nome de cliente e
    // valor de nota. Se um dia for ligado, precisa de mascaramento
    // conferido campo a campo, não do padrão.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,

    sendDefaultPii: false,

    ignoreErrors: [
      // Ruído de extensão de navegador e de rede do usuário — não é
      // defeito do sistema e afoga o que importa.
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      "Non-Error promise rejection captured",
      /^Failed to fetch$/,
      /^NetworkError/,
      /extension\//i,
    ],

    beforeSend(evento) {
      if (evento.request) {
        evento.request.url = urlSemQuery(evento.request.url);
        delete evento.request.cookies;
        delete evento.request.headers;
      }
      if (evento.extra)   evento.extra   = limpar(evento.extra);
      if (evento.contexts) evento.contexts = limpar(evento.contexts);
      return evento;
    },

    beforeBreadcrumb(migalha) {
      // Breadcrumb de fetch carrega a URL do Supabase com filtro — e o
      // filtro costuma trazer código de cliente e número de nota.
      if (migalha.data?.url) migalha.data.url = urlSemQuery(migalha.data.url);
      // Digitação em campo de formulário não interessa e pode conter
      // dado do cliente.
      if (migalha.category === "ui.input") return null;
      return migalha;
    },
  });
}

/**
 * Identifica o usuário por id e função — nunca por e-mail ou nome.
 *
 * Saber que "o erro atingiu 3 usuários da filial Maringá" é o que ajuda a
 * priorizar. Saber quem são, nominalmente, não muda o conserto.
 */
export function identificarUsuario({ id, cargo, filial } = {}) {
  S?.setUser(id ? { id, cargo, filial } : null);
}

/** Marca a tela atual, para o erro chegar com o contexto de onde ocorreu. */
export function marcarTela(nome) {
  S?.setTag("tela", nome);
}

/**
 * OpenTelemetry — carregado só quando há endpoint configurado.
 *
 * O import é dinâmico de propósito: sem endpoint, nenhum byte de OTel
 * entra no bundle inicial. É o mesmo raciocínio do lazy loading da #13.
 */
export async function iniciarTracing() {
  if (!OTLP) return;

  const [
    { WebTracerProvider, BatchSpanProcessor },
    { OTLPTraceExporter },
    { registerInstrumentations },
    { FetchInstrumentation },
    { resourceFromAttributes },
  ] = await Promise.all([
    import("@opentelemetry/sdk-trace-web"),
    import("@opentelemetry/exporter-trace-otlp-http"),
    import("@opentelemetry/instrumentation"),
    import("@opentelemetry/instrumentation-fetch"),
    import("@opentelemetry/resources"),
  ]);

  const provider = new WebTracerProvider({
    resource: resourceFromAttributes({
      "service.name": "tms-gamma-web",
      "service.version": VERSAO,
      "deployment.environment": AMBIENTE,
    }),
    spanProcessors: [
      new BatchSpanProcessor(new OTLPTraceExporter({ url: OTLP })),
    ],
  });

  provider.register();

  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation({
        // Sem isso o trace não cruza a fronteira até o Supabase.
        propagateTraceHeaderCorsUrls: [/supabase\.co/],
        // Mesmo cuidado do Sentry: a query traz filtro com dado de cliente.
        applyCustomAttributesOnSpan(span, request) {
          const url = request?.url || request;
          span.setAttribute("http.url", urlSemQuery(url));
        },
      }),
    ],
  });
}

/**
 * Reporta um erro. Devolve o id do evento, que vira o número do chamado.
 *
 * Silenciosa quando o Sentry não está carregado — sem DSN, ou nos
 * milissegundos antes do import dinâmico terminar. Quem chama não precisa
 * saber em qual dos dois estados está.
 */
export function capturarErro(erro, contexto) {
  try {
    return S?.captureException?.(erro, contexto);
  } catch {
    return undefined;
  }
}
